import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { AccountBrand } from "@/screens/analysis/types";
import { RiAddLargeLine, RiCheckLine, RiExternalLinkLine } from "@remixicon/react";
import { cn } from "@/lib/utils";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import { SqButton } from "@/components/squircle-ui/button";
import facebookIconColor from "@/assets/icons/facebook.svg";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { throttle } from "lodash";

type BrandRowProps = {
  brand: AccountBrand;
  onSelect: (brand: AccountBrand) => void;
  isSelected?: boolean;
  isAdded?: boolean;
};

const BrandRow: React.FC<BrandRowProps> = ({
  brand,
  onSelect,
  isSelected = false,
  isAdded = false,
}) => {
  const handleSelect = () => {
    onSelect(brand);
  };

  return (
    <div
      className={`group w-full h-[34px] p-[6px] flex items-center justify-between rounded-[6px] ${isSelected ? "bg-selected-gray" : "hover:bg-hover-gray"}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Brand Logo */}
        <div className="w-[18px] h-[18px] flex-shrink-0 rounded-[6px] overflow-hidden flex items-center justify-center">
          {brand.profile_image_url ? (
            <img
              src={brand.profile_image_url}
              alt={brand.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-gray-500">
                {brand.name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
          )}
        </div>

        {/* Brand Name */}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium text-brand-primary-new/70 truncate">
            {brand.name}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-[6px]">
          <Tooltip>
            <TooltipTrigger asChild>
              <SqButton
                variant="outline"
                size={"sm"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(brand.ad_library_url, "_blank");
                }}
                className="hidden group-hover:flex bg-white rounded-[8px]"
              >
                <div className="flex items-center gap-[7px]">
                  <img
                    src={facebookIconColor}
                    alt="Facebook"
                    className="w-[14px] h-[14px]"
                  />
                </div>
                <RiExternalLinkLine className="w-[14px] h-[14px] text-brand-primary-400" />
              </SqButton>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>Open in Ad library</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <SqButton
                size={"sm"}
                variant={"outline"}
                onClick={handleSelect}
                disabled={isAdded}
                className={cn(
                  "hidden group-hover:flex w-[28px] bg-white rounded-[8px]",
                  isSelected && "flex",
                )}
              >
                {isSelected ? (
                  <RiCheckLine size={14} className="text-brand-orange shrink-0" />
                ) : (
                  <RiAddLargeLine size={14} className="text-brand-primary-400 shrink-0" />
                )}
              </SqButton>
            </TooltipTrigger>
            <TooltipContent side="top" align="center" className="z-50">
              <p>
                {isAdded
                  ? "You've already added this brand."
                  : isSelected
                    ? "Deselect Brand"
                    : "Select Brand"}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Ad Count */}
        <div className="text-[12px] text-brand-primary-new/70">
          {brand.ad_count.toLocaleString()} Ads
        </div>
      </div>
    </div>
  );
};

type Props = {
  brands: AccountBrand[];
  categoryName?: string;
  isLoading?: boolean;
  onSelectBrand: (brand: AccountBrand) => void;
  selectedBrands?: AccountBrand[];
  className?: string;
  hasNext?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  addedBrands?: AccountBrand[];
  totalCount?: number;
};

const CategoryBrandsList: React.FC<Props> = ({
  brands,
  categoryName,
  isLoading = false,
  onSelectBrand,
  selectedBrands = [],
  className,
  hasNext = false,
  onLoadMore,
  isLoadingMore = false,
  addedBrands = [],
  totalCount = 0,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  // Check if content is scrollable and if user is at bottom
  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const isScrollable = container.scrollHeight > container.clientHeight;
      setCanScroll(isScrollable);

      // Check if user is at the bottom
      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 5; // Small threshold for "at bottom" detection
      const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      setIsAtBottom(atBottom);
    }
  }, []);

  // Throttled scroll handler for better performance
  const throttledHandleScroll = useMemo(
    () =>
      throttle(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Check scrollability and bottom position
        checkScrollability();

        // Handle infinite loading
        if (!hasNext || isLoadingMore || !onLoadMore) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 100; // Load more when 100px from bottom

        if (scrollTop + clientHeight >= scrollHeight - threshold) {
          onLoadMore();
        }
      }, 100), // Throttle to max 10 calls per second
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNext, isLoadingMore, onLoadMore],
  );

  // Check scrollability when brands change
  useEffect(() => {
    checkScrollability();
  }, [brands, checkScrollability]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", throttledHandleScroll);
      return () => {
        container.removeEventListener("scroll", throttledHandleScroll);
        throttledHandleScroll.cancel(); // Cancel any pending throttled calls
      };
    }
  }, [throttledHandleScroll]);
  if (isLoading) {
    return (
      <div
        className={cn("h-full w-full flex items-center justify-center py-8", className)}
      >
        <WaveLoader />
      </div>
    );
  }

  if (!brands || brands.length === 0) {
    return (
      <div className={cn("w-full h-full flex items-center justify-center", className)}>
        <NothingToShow showDescription={false} />
      </div>
    );
  }

  // Split brands into two columns
  const leftColumnBrands = brands.filter((_, index) => index % 2 === 0);
  const rightColumnBrands = brands.filter((_, index) => index % 2 === 1);

  return (
    <div className={cn("flex flex-col h-full w-full", className)}>
      {/* Header */}
      {categoryName && (
        <div className="px-[6px] py-2 text-[13px] font-medium text-brand-primary-800">
          {categoryName}{" "}
          <span className="ml-2 font-normal text-brand-primary-400">
            {totalCount} Brands
          </span>
        </div>
      )}

      {/* Two Column Layout with Scroll */}
      <div className="relative flex-1 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="grid grid-cols-2 gap-x-[28px] h-full overflow-y-auto"
        >
          {/* Left Column */}
          <div className="space-y-[2px]">
            {leftColumnBrands.map((brand) => (
              <BrandRow
                key={brand.id}
                brand={brand}
                onSelect={onSelectBrand}
                isSelected={selectedBrands.some((b) => b.id === brand.id)}
                isAdded={addedBrands.some((b) => b.id === brand.id)}
              />
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-[2px]">
            {rightColumnBrands.map((brand) => (
              <BrandRow
                key={brand.id}
                brand={brand}
                onSelect={onSelectBrand}
                isSelected={selectedBrands.some((b) => b.id === brand.id)}
                isAdded={addedBrands.some((b) => b.id === brand.id)}
              />
            ))}
          </div>
          {/* Loading indicator for more items */}
          {isLoadingMore && (
            <div className="col-span-2 w-full h-[40px] overflow-hidden flex items-center justify-center">
              <WaveLoader className="w-[100px]" />
            </div>
          )}
        </div>

        {/* Blur effect at bottom when scrollable and not at bottom */}
        {canScroll && !isAtBottom && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[40px] pointer-events-none z-10"
            style={{
              background:
                "linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CategoryBrandsList;
